/**
 * Persona Selector Component (Reusable)
 *
 * Grouped dropdown for selecting eval personas. System (CEFR) personas
 * are shown first, then custom. Description preview on hover via title.
 *
 * Story: 24.1 - Lab Foundation & Eval Personas
 */

import { useEffect } from 'react';
import { useEvalPersonas, type EvalPersona } from '../services/personaApi';

interface PersonaSelectorProps {
  episodeComplexityLevel?: number;
  selectedPersonaId: number | null;
  onPersonaSelect: (persona: EvalPersona) => void;
}

/** Map complexity level (1-5) to CEFR code */
const COMPLEXITY_TO_CEFR: Record<number, string> = {
  1: 'A1',
  2: 'A2',
  3: 'B1',
  4: 'B2',
  5: 'C1',
};

export function PersonaSelector({
  episodeComplexityLevel,
  selectedPersonaId,
  onPersonaSelect,
}: PersonaSelectorProps) {
  const { data: personas, isLoading } = useEvalPersonas();

  const systemPersonas = personas?.filter((p) => p.isSystem) || [];
  const customPersonas = personas?.filter((p) => !p.isSystem) || [];

  // Auto-select default persona matching episode complexity
  useEffect(() => {
    if (!personas || selectedPersonaId) return;

    if (episodeComplexityLevel) {
      const cefrCode = COMPLEXITY_TO_CEFR[episodeComplexityLevel];
      const match = systemPersonas.find((p) => p.cefrCode === cefrCode);
      if (match) {
        onPersonaSelect(match);
        return;
      }
    }

    // Fallback: select first system persona (B1 = id 3 typically)
    const fallback = systemPersonas.find((p) => p.cefrCode === 'B1') || systemPersonas[0];
    if (fallback) {
      onPersonaSelect(fallback);
    }
  }, [personas, selectedPersonaId, episodeComplexityLevel, systemPersonas, onPersonaSelect]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const persona = personas?.find((p) => p.id === id);
    if (persona) {
      onPersonaSelect(persona);
    }
  };

  if (isLoading) {
    return (
      <select disabled className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100">
        <option>Loading personas...</option>
      </select>
    );
  }

  return (
    <select
      value={selectedPersonaId || ''}
      onChange={handleChange}
      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      <option value="" disabled>
        Select a persona...
      </option>
      {systemPersonas.length > 0 && (
        <optgroup label="System (CEFR)">
          {systemPersonas.map((p) => (
            <option key={p.id} value={p.id} title={p.description}>
              {p.name}
            </option>
          ))}
        </optgroup>
      )}
      {customPersonas.length > 0 && (
        <optgroup label="Custom">
          {customPersonas.map((p) => (
            <option key={p.id} value={p.id} title={p.description}>
              {p.name}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}
